import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle, 
  FileText, 
  RefreshCw,
  ArrowLeft,
  FileAudio,
  Loader2
} from 'lucide-react';
import { useTheme } from '../zustand/ThemeStore';
import { useUserStore } from '../zustand/UserStore';
import { getApiBaseUrl } from '../utils/config';

interface JobResult {
  id: string;
  filename: string;
  file_type: 'audio' | 'transcript';
  customer_name: string;
  status: 'completed' | 'failed';
  error?: string;
}

interface JobData {
  job_id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  results: JobResult[];
  representative_id: string;
  completed_at: string;
  total_files: number;
  processed_files: number;
  progress_step?: 'processing_files' | 'generating_transcript' | 'analyzing_call';
  progress_message?: string;
}

const JobResultsPage: React.FC = () => {
  const isDarkMode = useTheme((state) => state.theme);
  const currentUser = useUserStore((state) => state.user);
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  
  const [jobData, setJobData] = useState<JobData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [loadingFile, setLoadingFile] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) {
      navigate('/analysis');
      return;
    }
    
    if (!currentUser) {
      return; // Wait for currentUser to be loaded
    }
    
    fetchJobResults();
  }, [jobId, currentUser, navigate]);

  const fetchJobResults = async () => {
    if (!jobId || !currentUser) return;
    
    try {
      setLoading(true);
      
      // First check job status
      const statusResponse = await axios.get(`${getApiBaseUrl()}/api/v1/jobs/${jobId}?user_id=${currentUser.uid}`);
      const jobStatus = statusResponse.data;
      
      if (jobStatus.status !== 'completed') {
        // Job not yet completed, show status and option to wait
        setJobData({
          ...jobStatus,
          results: []
        });
        setLoading(false);
        
        if (jobStatus.status === 'processing' || jobStatus.status === 'queued') {
          toast.info(`Job is ${jobStatus.status}`, {
            description: 'Results will appear when analysis completes'
          });
        }
        return;
      }
      
      // Job completed, fetch results
      const resultsResponse = await axios.get(`${getApiBaseUrl()}/api/v1/jobs/${jobId}/results?user_id=${currentUser.uid}`);
      setJobData(resultsResponse.data);
      setLoading(false);
      
    } catch (err: any) {
      console.error('Error fetching job results:', err);
      setError(err?.response?.data?.detail || 'Failed to fetch job results');
      setLoading(false);
      
      toast.error('Failed to load results', {
        description: 'Unable to fetch job results. Please try again.'
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'queued':
        return <Clock className="w-4 h-4 text-gray-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getFileIcon = (fileType: string) => {
    return fileType === 'audio' ? 
      <FileAudio className="w-6 h-6 text-[#1a9f6b]" /> :
      <FileText className="w-6 h-6 text-[#1a9f6b]" />;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return isDarkMode ? 'text-green-400' : 'text-green-600';
      case 'processing':
        return isDarkMode ? 'text-blue-400' : 'text-blue-600';
      case 'failed':
        return isDarkMode ? 'text-red-400' : 'text-red-600';
      default:
        return isDarkMode ? 'text-gray-400' : 'text-gray-600';
    }
  };

  const handleFileClick = async (result: JobResult) => {
    setSelectedFile(result.id);
    setLoadingFile(result.id);
    
    // Add small delay to show loading state
    setTimeout(() => {
      setLoadingFile(null);
      navigate(`/analysis/${result.id}`);
    }, 500);
  };

  const handleBackToAnalysis = () => {
    navigate('/analysis');
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#1a9f6b]" />
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading job results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="p-6">
          <div className="mb-6">
            <button
              onClick={handleBackToAnalysis}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                isDarkMode
                  ? 'bg-[#0a1414] hover:bg-[#0f1919] text-white'
                  : 'bg-white hover:bg-gray-100 text-gray-700'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Analysis Dashboard
            </button>
          </div>
          
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Error Loading Results
              </h1>
              <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {error}
              </p>
              <button
                onClick={handleBackToAnalysis}
                className="px-6 py-2 bg-[#1a9f6b] text-white rounded-lg hover:bg-[#168a5b] transition-colors"
              >
                Back to Analysis
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!jobData) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="p-6">
          <div className="mb-6">
            <button
              onClick={handleBackToAnalysis}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                isDarkMode
                  ? 'bg-[#0a1414] hover:bg-[#0f1919] text-white'
                  : 'bg-white hover:bg-gray-100 text-gray-700'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Analysis Dashboard
            </button>
          </div>
          
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center">
              <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                No job data available
              </p>
              <button
                onClick={handleBackToAnalysis}
                className={`px-4 py-2 rounded-lg transition-all ${
                  isDarkMode
                    ? 'bg-[#0a1414] hover:bg-[#0f1919] text-white'
                    : 'bg-white hover:bg-gray-100 text-gray-700'
                }`}
              >
                Back to Analysis Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isJobComplete = jobData.status === 'completed';
  const successfulResults = jobData.results?.filter(r => r.status === 'completed') || [];
  const failedResults = jobData.results?.filter(r => r.status === 'failed') || [];

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="p-6">
        <div className="mb-6">
          <button
            onClick={handleBackToAnalysis}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              isDarkMode
                ? 'bg-[#0a1414] hover:bg-[#0f1919] text-white'
                : 'bg-white hover:bg-gray-100 text-gray-700'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Analysis Dashboard
          </button>
        </div>

        {/* Job Status Info Bar */}
        <div className={`rounded-2xl p-4 mb-6 ${
          isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white border border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-100'
              }`}>
                {getStatusIcon(jobData.status)}
              </div>
              <div>
                <h2 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Job Status: {jobData.status.charAt(0).toUpperCase() + jobData.status.slice(1)}
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  ID: {jobData.job_id}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              {!isJobComplete && (
                <button
                  onClick={fetchJobResults}
                  className={`flex items-center gap-2 px-3 py-1 rounded-md text-xs font-medium transition-all shadow-sm border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1a9f6b] ${
                    isDarkMode
                      ? 'bg-[#1a2424] text-white border-[#1a9f6b] hover:bg-[#0f1919]'
                      : 'bg-white text-[#1a9f6b] border-[#1a9f6b] hover:bg-gray-100'
                  }`}
                >
                  <RefreshCw className="w-3 h-3" />
                  Refresh
                </button>
              )}
              <div className="text-right">
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {jobData.total_files}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Total Files
                </p>
              </div>
            </div>
          </div>
          
          <div className={`grid grid-cols-3 gap-4 mt-4 pt-4 border-t ${
            isDarkMode ? 'border-[#1a2424]' : 'border-gray-200'
          }`}>
            <div className="text-center">
              <p className="text-2xl font-bold text-[#1a9f6b]">{jobData.processed_files}</p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Processed</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-500">{successfulResults.length}</p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Successful</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-500">{failedResults.length}</p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Failed</p>
            </div>
          </div>
        </div>

        {/* Files Grid */}
        {isJobComplete && jobData.results && jobData.results.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {jobData.results.map((result: JobResult) => (
              <div
                key={result.id}
                onClick={() => handleFileClick(result)}
                className={`relative rounded-xl p-5 cursor-pointer transition-all transform hover:scale-[1.02] ${
                  isDarkMode
                    ? 'bg-[#0a1414] hover:bg-[#0f1919] border border-[#1a2424] hover:border-[#1a9f6b]'
                    : 'bg-white hover:bg-gray-50 border border-gray-200 hover:border-[#1a9f6b]'
                } ${selectedFile === result.id ? 'ring-2 ring-[#1a9f6b]' : ''}`}
              >
                {loadingFile === result.id && (
                  <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center z-10">
                    <Loader2 className="w-6 h-6 text-[#1a9f6b] animate-spin" />
                  </div>
                )}

                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${
                  isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-100'
                }`}>
                  {getFileIcon(result.file_type)}
                </div>

                <h3 className={`font-medium mb-2 truncate ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {result.filename}
                </h3>

                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Customer:
                    </span>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {result.customer_name}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Status:
                    </span>
                    <div className="flex items-center gap-1">
                      {getStatusIcon(result.status)}
                      <span className={`text-xs capitalize ${getStatusColor(result.status)}`}>
                        {result.status}
                      </span>
                    </div>
                  </div>
                  {result.error && (
                    <div className="mt-2">
                      <p className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                        {result.error}
                      </p>
                    </div>
                  )}
                  {/* Retry button for failed recordings */}
                  {result.status === 'failed' && (
                    <div className="flex justify-end mt-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileClick(result);
                        }}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-all shadow-sm border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1a9f6b] ${
                          isDarkMode
                            ? 'bg-[#1a2424] text-white border-[#1a9f6b] hover:bg-[#0f1919]'
                            : 'bg-white text-[#1a9f6b] border-[#1a9f6b] hover:bg-gray-100'
                        }`}
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : !isJobComplete ? (
          <div className={`rounded-2xl p-8 text-center ${
            isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white border border-gray-200'
          }`}>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-100'
            }`}>
              <Clock className="w-8 h-8 text-[#1a9f6b] animate-spin" />
            </div>
            <h3 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Analysis in Progress
            </h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Your files are being analyzed. This page will automatically update when complete.
            </p>
            
            {/* 3-Step Progress Indicator */}
            <div className="max-w-md mx-auto mt-6 space-y-3">
              {/* Step 1: Processing Files */}
              <div className={`flex items-center gap-3 p-3 rounded-lg ${
                jobData.progress_step === 'processing_files' 
                  ? isDarkMode ? 'bg-[#1a9f6b]/10 border border-[#1a9f6b]/30' : 'bg-[#1a9f6b]/10 border border-[#1a9f6b]/30'
                  : isDarkMode ? 'bg-[#0a1414]' : 'bg-gray-50'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  jobData.progress_step === 'processing_files' 
                    ? 'bg-[#1a9f6b] text-white'
                    : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                }`}>
                  {jobData.progress_step === 'processing_files' ? '📁' : '1'}
                </div>
                <span className={`text-sm font-medium ${
                  jobData.progress_step === 'processing_files' 
                    ? isDarkMode ? 'text-[#1a9f6b]' : 'text-[#1a9f6b]'
                    : isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Processing Files
                </span>
              </div>

              {/* Step 2: Generating Transcript */}
              <div className={`flex items-center gap-3 p-3 rounded-lg ${
                jobData.progress_step === 'generating_transcript' 
                  ? isDarkMode ? 'bg-[#1a9f6b]/10 border border-[#1a9f6b]/30' : 'bg-[#1a9f6b]/10 border border-[#1a9f6b]/30'
                  : isDarkMode ? 'bg-[#0a1414]' : 'bg-gray-50'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  jobData.progress_step === 'generating_transcript' 
                    ? 'bg-[#1a9f6b] text-white'
                    : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                }`}>
                  {jobData.progress_step === 'generating_transcript' ? '📝' : '2'}
                </div>
                <span className={`text-sm font-medium ${
                  jobData.progress_step === 'generating_transcript' 
                    ? isDarkMode ? 'text-[#1a9f6b]' : 'text-[#1a9f6b]'
                    : isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Generating Transcript
                </span>
              </div>

              {/* Step 3: Analyzing Call */}
              <div className={`flex items-center gap-3 p-3 rounded-lg ${
                jobData.progress_step === 'analyzing_call' 
                  ? isDarkMode ? 'bg-[#1a9f6b]/10 border border-[#1a9f6b]/30' : 'bg-[#1a9f6b]/10 border border-[#1a9f6b]/30'
                  : isDarkMode ? 'bg-[#0a1414]' : 'bg-gray-50'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  jobData.progress_step === 'analyzing_call' 
                    ? 'bg-[#1a9f6b] text-white'
                    : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                }`}>
                  {jobData.progress_step === 'analyzing_call' ? '🔍' : '3'}
                </div>
                <span className={`text-sm font-medium ${
                  jobData.progress_step === 'analyzing_call' 
                    ? isDarkMode ? 'text-[#1a9f6b]' : 'text-[#1a9f6b]'
                    : isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Analyzing Call
                </span>
              </div>
            </div>
            
            <p className={`text-sm mt-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              {jobData.progress_message || `Processing ${jobData.processed_files} of ${jobData.total_files} files`}
            </p>
          </div>
        ) : (
          <div className={`rounded-2xl p-8 text-center ${
            isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white border border-gray-200'
          }`}>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-100'
            }`}>
              <AlertCircle className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              No Results Available
            </h3>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              The analysis completed but no results are available to display.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobResultsPage;